/**
 * The design's own Spanish pass (aithora-es.js AithoraES.pass), for the React console: it
 * walks the text the page has drawn and swaps each line it has Spanish for, keeping the
 * English beside it so switching back restores every word. Pages are not rewritten; the
 * pass runs over whatever they render, and again over whatever changes (a MutationObserver).
 *
 * Never touched: anything under translate="no" (Truth Ledger text, Atlas answers, words that
 * go to a family or a doctor, names a page marks), form fields, scripts and styles. Names the
 * server sent are protected by the engine itself (harvestNames in @aithora/shared).
 */
import type { EsEngine } from '@aithora/shared';

type Kept = { en: string; es: string; joined?: boolean };
export type EsStore = WeakMap<Node, Kept>;
const ATTRS = ['placeholder', 'aria-label', 'title'] as const;
const attrStore = new WeakMap<Element, Record<string, Kept>>();

function skipped(el: Element | null): boolean {
  if (!el) return true;
  if (el.closest('[translate="no"],script,style,textarea,[contenteditable="true"]')) return true;
  return false;
}

function trFor(A: EsEngine, t: string, p: Element | null): string | null {
  let tr = A.tr(t);
  const ctx = A.ctx[t];
  if (ctx && p) {
    const box = p.parentElement?.parentElement;
    const txt = box ? box.textContent ?? '' : '';
    for (const c of ctx) if (c[0].test(txt)) { tr = c[1]; break; }
  }
  // The design shortens a few labels in the side nav ("System Pulse" -> "Pulse").
  if (A.navShort[t] && p?.closest('nav')) tr = A.navShort[t];
  return tr;
}

/** One text node: swap it to Spanish (on) or back to English (off). */
function passText(n: Text, on: boolean, store: EsStore, A: EsEngine | null): void {
  const cur = n.nodeValue ?? '';
  const o = store.get(n);
  if (!on) {
    if (o && o.es === cur) n.nodeValue = o.en;
    return;
  }
  if (!A) return;
  const t = cur.trim();
  if (t.length < 2) return;
  if (o && o.es === cur) return;
  const p = n.parentElement;
  if (skipped(p)) return;
  const tr = trFor(A, t, p);
  if (tr && tr !== t) {
    const v = cur.replace(t, tr);
    store.set(n, { en: cur, es: v });
    n.nodeValue = v;
  }
}

/** React can split one sentence into several adjacent text nodes ("Room ", "214"):
 *  translate the joined text and keep it in the first node, as the design does. */
function passJoined(el: Element, store: EsStore, A: EsEngine): void {
  const cn = el.childNodes;
  if (cn.length < 2) return;
  for (let i = 0; i < cn.length; i++) if (cn[i].nodeType !== 3) return;
  if (skipped(el)) return;
  const nodes = Array.from(cn) as Text[];
  if (nodes.every((x) => store.get(x)?.es === x.nodeValue)) return;
  const enOf = (x: Text) => {
    const o = store.get(x);
    return o && o.es === x.nodeValue ? o.en : x.nodeValue ?? '';
  };
  const full = nodes.map(enOf).join('');
  const t = full.trim();
  if (t.length < 2) return;
  const tr = A.tr(t);
  if (!tr || tr === t) {
    // The line changed and has no Spanish now: put back any English an earlier join held.
    nodes.forEach((x) => {
      const o = store.get(x);
      if (o?.joined && o.es === x.nodeValue) {
        store.delete(x);
        x.nodeValue = o.en;
      }
    });
    return;
  }
  nodes.forEach((x, i) => {
    const en = enOf(x);
    const es = i === 0 ? full.replace(t, tr) : '';
    store.set(x, { en, es, joined: true });
    x.nodeValue = es;
  });
}

function passAttrs(el: Element, on: boolean, A: EsEngine | null): void {
  for (const a of ATTRS) {
    const cur = el.getAttribute(a);
    if (!cur) continue;
    const kept = attrStore.get(el) ?? {};
    const o = kept[a];
    if (!on) {
      if (o && o.es === cur) el.setAttribute(a, o.en);
      continue;
    }
    if (!A || (o && o.es === cur) || skipped(el)) continue;
    const tr = A.tr(cur.trim());
    if (tr && tr !== cur) {
      kept[a] = { en: cur, es: tr };
      attrStore.set(el, kept);
      el.setAttribute(a, tr);
    }
  }
}

/** Swap everything under `root` to Spanish (on, with the engine) or back to English (off). */
export function esPass(root: Node, on: boolean, store: EsStore, A: EsEngine | null): void {
  if (root.nodeType === 3) {
    const p = root.parentElement;
    if (on && A && p) passJoined(p, store, A);
    passText(root as Text, on, store, A);
    return;
  }
  if (!(root instanceof Element) && !(root instanceof Document) && !(root instanceof DocumentFragment)) return;
  const doc = root.ownerDocument ?? (root as Document);
  if (on && A) {
    if (root instanceof Element) passJoined(root, store, A);
    root.querySelectorAll('*').forEach((el) => passJoined(el, store, A));
  }
  const w = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let n: Node | null;
  while ((n = w.nextNode())) passText(n as Text, on, store, A);
  if (root instanceof Element) passAttrs(root, on, A);
  root.querySelectorAll?.('[placeholder],[aria-label],[title]').forEach((el) => passAttrs(el, on, A));
}

/**
 * Keep `root` in Spanish while it changes: every node React adds or rewrites is passed on
 * the next frame. Returns a stop function. Our own swaps are dropped from the queue, so
 * the observer never feeds itself.
 */
export function watchEs(root: Element, store: EsStore, A: EsEngine): () => void {
  const dirty = new Set<Node>();
  let queued = false;
  const schedule = typeof requestAnimationFrame === 'function' ? requestAnimationFrame : (f: () => void) => setTimeout(f, 16);
  const obs = new MutationObserver((records) => {
    for (const r of records) {
      if (r.type === 'characterData') dirty.add(r.target);
      else if (r.type === 'attributes') dirty.add(r.target);
      else r.addedNodes.forEach((x) => dirty.add(x));
    }
    if (queued || dirty.size === 0) return;
    queued = true;
    schedule(() => {
      queued = false;
      const list = Array.from(dirty);
      dirty.clear();
      for (const x of list) if (x.isConnected) esPass(x, true, store, A);
      obs.takeRecords();
    });
  });
  obs.observe(root, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: [...ATTRS] });
  return () => obs.disconnect();
}
